import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { WorkflowLimitRow } from '@/lib/api/generated/model';

import { useClearWorkflowLimit } from '../hooks/useLimits';

/**
 * Confirms removing a workflow's cap (spec 080 FR-005).
 *
 * Follows `RemoveOverrideDialog`'s shape — state what happens, never "are you sure" —
 * but the consequence here has only one form, and it is the opposite of that dialog's.
 * Removing a workspace override makes the workspace fall back to the organization
 * default, which can *stop* it. A workflow cap inherits nothing: absence means no
 * workflow cap, so removing one can only ever release the workflow, never stop it.
 *
 * It remains bounded by its workspace, which is worth saying — otherwise "no limit"
 * reads as "unlimited", and this tier never made it unlimited.
 */
export function RemoveWorkflowLimitDialog({ row }: { row: WorkflowLimitRow }) {
  const [open, setOpen] = useState(false);
  const clearLimit = useClearWorkflowLimit();

  const close = (next: boolean) => {
    if (clearLimit.isPending) return;
    setOpen(next);
    if (!next) clearLimit.reset();
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Remove the limit for ${row.workflow_name}`}
          />
        }
      >
        Remove
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove the limit for {row.workflow_name}?</DialogTitle>
          <DialogDescription>
            {row.workflow_name} will be bounded only by {row.workspace_name}
            &rsquo;s own limit. It cannot stop this workflow — a workflow with
            no limit of its own is not unlimited, it simply has no second gate.
          </DialogDescription>
        </DialogHeader>

        {clearLimit.isError ? (
          <p role="alert" className="text-destructive text-sm">
            {clearLimit.error.message}
          </p>
        ) : null}

        <DialogFooter>
          <DialogClose
            render={
              <Button variant="outline" disabled={clearLimit.isPending} />
            }
          >
            Cancel
          </DialogClose>
          <Button
            variant="destructive"
            disabled={clearLimit.isPending}
            onClick={() => {
              clearLimit.mutate(row.workflow_id, {
                onSuccess: () => {
                  close(false);
                },
              });
            }}
          >
            {clearLimit.isPending ? 'Removing…' : 'Remove limit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
